// src/pages/LeadIntakeReport.jsx
// Full stats report — leads by source, conversion rates, daily trend

import React, { useState, useEffect } from 'react';
import { LeadIntake } from '../services/api';

const SOURCE_ICONS = {
    meta:'📘', google:'🔍', indiamart:'🏭',
    tradeindia:'🤝', justdial:'📞', manual:'📋', custom:'🔗',
};

export default function LeadIntakeReport() {
    const [data,   setData]   = useState(null);
    const [days,   setDays]   = useState(30);
    const [busy,   setBusy]   = useState(true);
    const [err,    setErr]    = useState('');

    useEffect(() => {
        setBusy(true);
        LeadIntake.stats(days)
            .then(setData)
            .catch(e => setErr(e.message))
            .finally(() => setBusy(false));
    }, [days]);

    if (busy) return <div className="spinner-wrap"><div className="spinner" /></div>;
    if (!data) return null;

    const { sources, totals, trend } = data;
    const maxCount = Math.max(...(trend.map(t => t.count)), 1);

    return (
        <div className="page">
            <div className="page-header">
                <div className="page-title">📊 Lead Intake Report</div>
                {/* Period selector */}
                <div style={{ display:'flex', gap:'.4rem' }}>
                    {[7, 14, 30, 90].map(d => (
                        <button key={d}
                            className={`btn btn-sm ${days===d ? 'btn-primary' : 'btn-ghost'}`}
                            onClick={() => setDays(d)}>
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {err && <div className="alert alert-error">{err}</div>}

            {/* ── Overall totals ── */}
            <div style={{ display:'flex', gap:'1rem', marginBottom:'1.5rem', flexWrap:'wrap' }}>
                {[
                    { label:'Total Leads',  count:totals.total,     color:'#374151', bg:'#f3f4f6' },
                    { label:'New',          count:totals.new_leads,  color:'#1e40af', bg:'#dbeafe' },
                    { label:'Converted',    count:totals.converted,  color:'#166534', bg:'#dcfce7' },
                    { label:'Duplicate',    count:totals.duplicate,  color:'#92400e', bg:'#fef3c7' },
                    { label:'Junk',         count:totals.junk,       color:'#6b7280', bg:'#f3f4f6' },
                ].map(s => (
                    <div key={s.label} style={{
                        background:s.bg, border:`1px solid ${s.color}30`,
                        borderRadius:12, padding:'10px 18px', minWidth:110,
                    }}>
                        <div style={{ fontSize:'.68rem', fontWeight:700, color:s.color,
                            textTransform:'uppercase', letterSpacing:'.06em' }}>{s.label}</div>
                        <div style={{ fontSize:'1.8rem', fontWeight:800, color:s.color }}>{s.count || 0}</div>
                        {s.label === 'Converted' && totals.total > 0 && (
                            <div style={{ fontSize:'.7rem', color:s.color }}>
                                {Math.round(totals.converted / totals.total * 100)}% rate
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem', marginBottom:'1.5rem' }}>

                {/* ── By source table ── */}
                <div style={{ background:'var(--card-bg,#121b32)', border:'1px solid var(--border,#e2e8f0)',
                    borderRadius:14, overflow:'hidden' }}>
                    <div style={{ padding:'.9rem 1.1rem', borderBottom:'1px solid var(--border,#e2e8f0)',
                        fontWeight:700, fontSize:'.9rem' }}>
                        Leads by Source
                    </div>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <thead>
                            <tr style={{ background:'var(--bg,#f8fafc)' }}>
                                {['Source','Total','New','Conv.','Rate'].map(h => (
                                    <th key={h} style={{ padding:'6px 12px', fontSize:'.7rem',
                                        textTransform:'uppercase', letterSpacing:'.06em',
                                        color:'var(--muted)', textAlign: h==='Source'?'left':'center',
                                        borderBottom:'1px solid var(--border,#e2e8f0)', fontWeight:600 }}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sources.map(s => (
                                <tr key={s.slug} style={{ borderBottom:'1px solid var(--border,#f1f5f9)' }}>
                                    <td style={{ padding:'8px 12px', fontSize:'.85rem', fontWeight:500 }}>
                                        {SOURCE_ICONS[s.slug] || '📌'} {s.source}
                                    </td>
                                    <td style={{ padding:'8px 12px', textAlign:'center', fontWeight:700 }}>{s.total}</td>
                                    <td style={{ padding:'8px 12px', textAlign:'center', color:'#1e40af' }}>{s.new_leads}</td>
                                    <td style={{ padding:'8px 12px', textAlign:'center', color:'#166534' }}>{s.converted}</td>
                                    <td style={{ padding:'8px 12px', textAlign:'center' }}>
                                        <span style={{
                                            background: s.conversion_rate >= 20 ? '#dcfce7' :
                                                        s.conversion_rate >= 10 ? '#fef3c7' : '#fee2e2',
                                            color:      s.conversion_rate >= 20 ? '#166534' :
                                                        s.conversion_rate >= 10 ? '#92400e' : '#991b1b',
                                            borderRadius:10, padding:'2px 8px',
                                            fontSize:'.72rem', fontWeight:700,
                                        }}>
                                            {s.conversion_rate}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            {sources.length === 0 && (
                                <tr><td colSpan={5} style={{ textAlign:'center', padding:'2rem',
                                    color:'var(--muted)', fontSize:'.875rem' }}>
                                    No leads in this period
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ── Daily trend bar chart ── */}
                <div style={{ background:'var(--card-bg,#121b32)', border:'1px solid var(--border,#e2e8f0)',
                    borderRadius:14, padding:'1rem 1.1rem' }}>
                    <div style={{ fontWeight:700, fontSize:'.9rem', marginBottom:'1rem' }}>
                        Daily Lead Volume (last 14 days)
                    </div>
                    {trend.length === 0 ? (
                        <div style={{ textAlign:'center', padding:'2rem', color:'var(--muted)' }}>
                            No data
                        </div>
                    ) : (
                        <div style={{ display:'flex', alignItems:'flex-end', gap:4,
                            height:140, paddingBottom:'1.5rem', position:'relative' }}>
                            {trend.map((t, i) => {
                                const pct = Math.max((t.count / maxCount) * 100, 4);
                                return (
                                    <div key={i} style={{ flex:1, display:'flex',
                                        flexDirection:'column', alignItems:'center', gap:2 }}>
                                        <div style={{ fontSize:'.65rem', color:'var(--muted)',
                                            fontWeight:600 }}>{t.count}</div>
                                        <div style={{
                                            width:'100%', height:`${pct}%`,
                                            background:'linear-gradient(180deg, #3b82f6, #1e40af)',
                                            borderRadius:'4px 4px 0 0',
                                            transition:'height .3s',
                                        }} title={`${t.date}: ${t.count} leads`} />
                                        <div style={{
                                            fontSize:'.6rem', color:'var(--muted)',
                                            transform:'rotate(-45deg)',
                                            transformOrigin:'top left',
                                            whiteSpace:'nowrap',
                                            marginTop:6, marginLeft:4,
                                        }}>
                                            {t.date.slice(5)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Source breakdown bars ── */}
            {sources.length > 0 && totals.total > 0 && (
                <div style={{ background:'var(--card-bg,#121b32)', border:'1px solid var(--border,#e2e8f0)',
                    borderRadius:14, padding:'1rem 1.25rem' }}>
                    <div style={{ fontWeight:700, fontSize:'.9rem', marginBottom:'1rem' }}>
                        Source Share
                    </div>
                    {sources.map(s => {
                        const pct = Math.round((s.total / totals.total) * 100);
                        return (
                            <div key={s.slug} style={{ marginBottom:'.75rem' }}>
                                <div style={{ display:'flex', justifyContent:'space-between',
                                    fontSize:'.8rem', marginBottom:'.25rem' }}>
                                    <span>{SOURCE_ICONS[s.slug]} {s.source}</span>
                                    <span style={{ color:'var(--muted)' }}>{s.total} leads ({pct}%)</span>
                                </div>
                                <div style={{ height:8, background:'var(--bg,#f1f5f9)',
                                    borderRadius:4, overflow:'hidden' }}>
                                    <div style={{
                                        height:'100%', width:`${pct}%`,
                                        background:'linear-gradient(90deg, #3b82f6, #1e40af)',
                                        borderRadius:4, transition:'width .5s',
                                    }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
